import { clearSessionRefreshFailureSuppression } from "@/lib/auth/sessionRefresh";
import { ensureSuperTokensInitialized } from "@/lib/auth/supertokensInit";
import { logger } from "@/lib/logger";
import {
  AntiCsrfToken,
  FrontToken,
  fireSessionUpdateEventsIfNecessary,
  getLocalSessionState,
  setToken,
  updateClockSkewUsingFrontToken
} from "supertokens-website/lib/build/fetch";

type SessionResponseHeaders = Headers | Record<string, string | undefined> | undefined;

function normalizeResponseHeaders(headers: SessionResponseHeaders): Headers {
  if (headers instanceof Headers) {
    return headers;
  }

  const normalized = new Headers();
  if (!headers) {
    return normalized;
  }

  Object.entries(headers).forEach(([key, value]) => {
    if (typeof value === "string" && value.length > 0) {
      normalized.set(key, value);
    }
  });

  return normalized;
}

export async function persistSuperTokensSessionFromResponse({
  headers,
  source,
  status = 200,
  fireSessionUpdateEvent = true,
  requireRefreshToken = true
}: {
  headers: SessionResponseHeaders;
  source: string;
  status?: number;
  fireSessionUpdateEvent?: boolean;
  requireRefreshToken?: boolean;
}) {
  ensureSuperTokensInitialized();

  const responseHeaders = normalizeResponseHeaders(headers);
  const accessToken = responseHeaders.get("st-access-token");
  const refreshToken = responseHeaders.get("st-refresh-token");
  const frontToken = responseHeaders.get("front-token");

  if (!accessToken || !frontToken || (requireRefreshToken && !refreshToken)) {
    logger.error("sessionPersist: auth response missing required session headers", {
      source,
      status,
      hasAccessToken: Boolean(accessToken),
      hasRefreshToken: Boolean(refreshToken),
      hasFrontToken: Boolean(frontToken),
      requireRefreshToken
    });
    throw new Error("Authentication response missing required session headers");
  }

  if (refreshToken) {
    await setToken("refresh", refreshToken);
  }
  await setToken("access", accessToken);
  await FrontToken.setItem(frontToken);
  updateClockSkewUsingFrontToken({ frontToken, responseHeaders });

  const antiCsrfToken = responseHeaders.get("anti-csrf");
  if (antiCsrfToken) {
    const localSessionState = await getLocalSessionState(false);
    if (localSessionState.status === "EXISTS") {
      await AntiCsrfToken.setItem(localSessionState.lastAccessTokenUpdate, antiCsrfToken);
    }
  }

  if (fireSessionUpdateEvent) {
    fireSessionUpdateEventsIfNecessary(false, status, frontToken);
  }

  clearSessionRefreshFailureSuppression();

  logger.debug("sessionPersist: saved session from auth response", {
    source,
    status,
    hasAntiCsrfToken: Boolean(antiCsrfToken),
    fireSessionUpdateEvent
  });
}
